import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators';

import { NewPrice, SimSearchVehicle } from 'src/app/api/models';
import { EnvironmentalBonusService } from 'src/app/core/services/environmental-bonus/environmental-bonus.service';
import {
  FootnoteHintsService,
  VehiclePropertyFootnotesConfig
} from 'src/app/core/services/footnote-hints/footnote-hints.service';
import { FootnotesService } from 'src/app/core/services/footnotes/footnotes.service';
import { ImageSizeType } from 'src/app/core/services/image-url/image-size-type.enum';
import { ImageSources } from 'src/app/core/services/image-url/image-sources';
import { ImageUrlService } from 'src/app/core/services/image-url/image-url.service';
import { LazyModuleId } from 'src/app/core/services/lazy-modules/lazy-modules-registry';
import { MarketConfigService } from 'src/app/core/services/market-config/market-config.service';
import { TrackingAction } from 'src/app/core/services/tracking/models/tracking-action.enum';
import { TrackingCategory } from 'src/app/core/services/tracking/models/tracking-category.enum';
import { TrackingEventDetail } from 'src/app/core/services/tracking/models/tracking-object.interface';
import { TrackingDataService } from 'src/app/core/services/tracking/tracking-data.service';
import { Vehicle } from 'src/app/core/vehicle/vehicle.interface';
import { ConfigurationService } from 'src/app/features/configuration/configuration.service';
import { filterChanged, filterTruthy } from 'src/app/utils/functions/rx-filters';
import { discountedOrConfigurationPrice, PriceInformation } from 'src/app/utils/models/price-information';

import { ModalService } from '../../modal/modal-service/modal.service';
import { SimsearchService } from '../simsearch.service';

const MATCH_FACTOR_ANIMATION_STEP_SIZE = 4;
const PREVIEW_IMAGE_SIZE_IN_PX = 200;

@Component({
  selector: 'cc-simsearch-view',
  templateUrl: './simsearch-view.component.html',
  styleUrls: ['./simsearch-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SimsearchViewComponent implements OnDestroy, OnInit {
  @Input() public vehicleId: string;

  @ViewChild('scrollContainer') private readonly scrollContainer: ElementRef;

  public stateVehicleId: string;
  public vehicleConfiguration: SimSearchVehicle;
  public similarVehicles: SimSearchVehicle[];
  public simsearchColumns = 0;
  public environmentalBonusFootnote: string;
  public offerDealerContact$: Observable<boolean> = of(true);

  private trackingParameter: string;
  private readonly unsubscribeSubject$ = new Subject<void>();

  public constructor(
    private readonly simsearchService: SimsearchService,
    private readonly configurationService: ConfigurationService,
    private readonly footnotesService: FootnotesService,
    private readonly footnoteHintsService: FootnoteHintsService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly trackingDataService: TrackingDataService,
    private readonly imageUrlService: ImageUrlService,
    private readonly modalService: ModalService,
    private readonly environmentalBonusService: EnvironmentalBonusService,
    private readonly marketConfigService: MarketConfigService
  ) {}

  public ngOnInit(): void {
    this.configurationService.vehicle$.pipe(takeUntil(this.unsubscribeSubject$)).subscribe((vehicle: Vehicle) => {
      this.vehicleId = vehicle.vehicleId;
      this.changeDetectorRef.markForCheck();

      this.offerDealerContact$ = this.marketConfigService.observe<boolean>('simsearch.offerDealerContact', true);

      this.trackingDataService
        .getSimsearchTrackingParameter(vehicle)
        .pipe(first())
        .subscribe(trackingParameter => {
          this.trackingParameter = trackingParameter;
        });
    });

    this.simsearchService.vehicleId$.pipe(filterTruthy(), takeUntil(this.unsubscribeSubject$)).subscribe(vehicleId => {
      this.stateVehicleId = vehicleId;
      this.changeDetectorRef.markForCheck();
    });

    this.simsearchService.footnotes$
      .pipe(filterTruthy(), takeUntil(this.unsubscribeSubject$))
      .subscribe(simsearchFootnotes => {
        Object.keys(simsearchFootnotes)?.forEach(hint => {
          this.footnotesService.registerFootnote(hint, simsearchFootnotes[hint], 'simsearch');
        });
        this.changeDetectorRef.markForCheck();
      });

    combineLatest([
      this.simsearchService.vehicleConfiguration$.pipe(filterTruthy()),
      this.simsearchService.similarVehicles$.pipe(filterTruthy())
    ])
      .pipe(filterChanged(), takeUntil(this.unsubscribeSubject$))
      .subscribe(([currentConfiguration, similarVehicles]: [SimSearchVehicle, SimSearchVehicle[]]): void => {
        this.vehicleConfiguration = [currentConfiguration].map(this.updateVehicle.bind(this))[0] as SimSearchVehicle;
        this.similarVehicles = similarVehicles.map(this.updateVehicle.bind(this));
        this.simsearchColumns = 1 + similarVehicles.length;
        this.updateVehicleLocations();

        const footnoteMapping: VehiclePropertyFootnotesConfig[] = this.getFootnoteMapping([
          currentConfiguration,
          ...similarVehicles
        ]);
        this.footnoteHintsService.registerFootnoteHintConfig('simsearch', footnoteMapping);

        this.changeDetectorRef.detectChanges();
      });

    if (this.stateVehicleId !== this.vehicleId) {
      this.simsearchService.startLoadVehiclesAction(this.vehicleId);
    }

    this.environmentalBonusService
      .registerFootnote$()
      .subscribe(footnote => (this.environmentalBonusFootnote = footnote));
  }

  public ngOnDestroy(): void {
    this.unsubscribeSubject$.next();
  }

  public onInViewport({ target, visible }: { target: HTMLElement; visible: boolean }, vehicle: SimSearchVehicle): void {
    const degrees = Math.floor(3.6 * Number(target.getAttribute('matchfactor'))); // x% of 360 degrees
    const matchfactorElement: HTMLElement = target;
    const activeBorderElement: HTMLElement = target.querySelector(
      '.cc-simsearch-view__table-cell__matchfactor--active-border'
    );
    const percentElement: HTMLElement = target.querySelector('.cc-simsearch-view__table-cell__matchfactor--percent');

    if (!!visible) {
      matchfactorElement.setAttribute('direction', 'up');
      this.matchfactorAnimation(matchfactorElement, activeBorderElement, percentElement, degrees);

      if (!matchfactorElement.getAttribute('tracked')) {
        this.simsearchService.callInViewportAction(vehicle);
        matchfactorElement.setAttribute('tracked', 'true');
      }
    } else {
      matchfactorElement.setAttribute('direction', 'down');
      this.matchfactorAnimation(matchfactorElement, activeBorderElement, percentElement, degrees);
    }
  }

  public onScroll(): void {
    this.changeDetectorRef.markForCheck();
  }

  public scroll(direction: 'right' | 'left'): void {
    if (!this.isScrolled(direction)) {
      const element = this.scrollContainer.nativeElement;
      const firstCell: HTMLElement = element.querySelector('.cc-simsearch-view__table-cell');

      if (!!firstCell) {
        // detect current Index, but definiely the last index if end of scrollbar reached
        let index = this.scrollContainer.nativeElement.scrollLeft / firstCell.scrollWidth;
        if (element.scrollLeft + element.offsetWidth >= element.scrollWidth) {
          index = Math.ceil(index);
        } else {
          index = Math.round(index);
        }

        this.animateScroll(direction, firstCell, index);
      }
    }
  }

  public isScrolled(direction: 'right' | 'left'): boolean {
    // let's say yes, if there is no scroll container; assuming we reached the and of nothing
    if (!this.scrollContainer || !this.scrollContainer.nativeElement) {
      return true;
    }

    if (direction === 'left') {
      return this.scrollContainer.nativeElement.scrollLeft === 0;
    }

    if (direction === 'right') {
      return (
        this.scrollContainer.nativeElement.scrollLeft >=
        this.scrollContainer.nativeElement.scrollWidth - this.scrollContainer.nativeElement.offsetWidth
      );
    }

    return false;
  }

  public getPricesFootnotes(vehicle: SimSearchVehicle): VehiclePropertyFootnotesConfig[] {
    const footnoteMapping: VehiclePropertyFootnotesConfig[] = this.getFootnoteMapping(vehicle);

    return footnoteMapping.filter(mapping => mapping.properties.includes('prices.price'));
  }

  public trackByFootnote(
    vehicle: SimSearchVehicle,
    index: number,
    item: VehiclePropertyFootnotesConfig
  ): string | void {
    if (!vehicle || !item) {
      return;
    }

    return item.footnote;
  }

  public getTrackingParameter(vehicle: SimSearchVehicle): string {
    if (!vehicle.link?.includes('vsStockVehicle')) {
      return (vehicle.link.includes('?') ? '&' : '?') + this.trackingParameter;
    }
    return '';
  }

  public getTrackingData(trackingAction: number): TrackingEventDetail {
    return {
      category: TrackingCategory.Link,
      label: !trackingAction ? TrackingAction.ContactDealer : `${TrackingAction.SimSearchDetails}_${trackingAction}`,
      action: TrackingAction.SimSearch
    };
  }

  public openContactMBPartnerModal(): void {
    this.modalService.open(LazyModuleId.ContactDealer);
  }

  public discountedOrConfigurationPrice(priceInformation: PriceInformation): NewPrice {
    return discountedOrConfigurationPrice(priceInformation);
  }

  private animateScroll(direction: 'right' | 'left', firstCell: HTMLElement, currentIndex: number): void {
    const distancePerFrame = 10;
    const element = this.scrollContainer.nativeElement;

    const delta = distancePerFrame * (direction === 'left' ? -1 : 1);

    if (direction === 'right') {
      const reachedEndOfTile = element.scrollLeft >= firstCell.scrollWidth * (currentIndex + 1);
      const reachedEndOfScrollbar = element.scrollLeft + element.offsetWidth >= element.scrollWidth;
      if (reachedEndOfTile || reachedEndOfScrollbar) {
        return;
      }
    }

    if (direction === 'left') {
      const reachedStartOfTile = element.scrollLeft <= firstCell.scrollWidth * (currentIndex - 1);
      const reachedStartOfScrollbar = this.scrollContainer.nativeElement.scrollLeft <= 0;
      if (reachedStartOfTile || reachedStartOfScrollbar) {
        return;
      }
    }

    requestAnimationFrame(() => {
      this.scrollContainer.nativeElement.scrollLeft += delta;
      this.animateScroll(direction, firstCell, currentIndex);
    });
  }

  private updateVehicle(
    vehicle: SimSearchVehicle
  ): SimSearchVehicle | (SimSearchVehicle & { imageSources: ImageSources }) {
    return this.setImageSources(vehicle);
  }

  private setImageSources(
    vehicle: SimSearchVehicle
  ): SimSearchVehicle | (SimSearchVehicle & { imageSources: ImageSources }) {
    if (!!vehicle.vehicleFrontImage) {
      (vehicle as SimSearchVehicle & { imageSources: ImageSources }) = {
        ...vehicle,
        imageSources: this.imageUrlService.formatResponsive(
          vehicle.vehicleFrontImage,
          ImageSizeType.ThreeToOneColumns,
          { isCeImage: true }
        )
      };

      // Instead of fetching huge images it's sufficient to fetch them in a smaller size - independent from the MQ
      (vehicle as any).imageSources.sizes = `${PREVIEW_IMAGE_SIZE_IN_PX}px`;
    }

    return vehicle;
  }

  private updateVehicleLocations(): void {
    const haveLocation = this.similarVehicles.some((vehicle: SimSearchVehicle): boolean => !!vehicle.location?.length);

    if (haveLocation) {
      this.vehicleConfiguration = {
        ...this.vehicleConfiguration,
        location: null
      };
      this.similarVehicles = this.similarVehicles.map(
        (vehicle: SimSearchVehicle): SimSearchVehicle => ({
          ...vehicle,
          location: !!vehicle.location?.length ? vehicle.location : '-'
        })
      );
    }
  }

  private matchfactorAnimation(
    matchfactorElement: HTMLElement,
    activeBorderElement: HTMLElement,
    percentElement: HTMLElement,
    degrees: number
  ): void {
    let i = Number(matchfactorElement.getAttribute('step') || 0);
    const direction: string = matchfactorElement.getAttribute('direction') || 'up';

    i += direction === 'up' ? MATCH_FACTOR_ANIMATION_STEP_SIZE : -MATCH_FACTOR_ANIMATION_STEP_SIZE;
    i = Math.max(Math.min(degrees, i), 0);

    matchfactorElement.setAttribute('step', String(i));

    const percent = Math.ceil((100 * i) / 360);
    percentElement.innerHTML = `${Math.round(percent)} %`;

    if (i <= 180) {
      activeBorderElement.style.backgroundImage = `linear-gradient(${
        90 + i
      }deg, transparent 50%, #e5e5e5 50%),linear-gradient(90deg, #e5e5e5 50%, transparent 50%)`;
    } else {
      activeBorderElement.style.backgroundImage = `linear-gradient(${
        i - 90
      }deg, transparent 50%, #00adef 50%),linear-gradient(90deg, #e5e5e5 50%, transparent 50%)`;
    }

    if ((direction === 'up' && i < degrees) || (direction === 'down' && i > 0)) {
      requestAnimationFrame((): void => {
        this.matchfactorAnimation(matchfactorElement, activeBorderElement, percentElement, degrees);
      });
    }
  }

  private getFootnoteMapping(vehicles: SimSearchVehicle | SimSearchVehicle[]): VehiclePropertyFootnotesConfig[] {
    const temporaryFootnoteMapping: { [key: string]: string[] } = {};
    (Array.isArray(vehicles) ? vehicles : [vehicles])?.forEach((vehicle: SimSearchVehicle): void => {
      vehicle.motorization?.forEach((dataSet: any): void =>
        this.footnotesToMap.call(this, dataSet, temporaryFootnoteMapping)
      );
      vehicle.categories?.forEach((category: any): void =>
        this.footnotesToMap.call(this, category, temporaryFootnoteMapping)
      );
    });
    return Object.keys(temporaryFootnoteMapping).map(
      (key: string): VehiclePropertyFootnotesConfig => ({
        footnote: key,
        properties: temporaryFootnoteMapping[key],
        values: []
      })
    );
  }

  private footnotesToMap(dataSet: any, temporaryFootnoteMapping: any[]): void {
    if (Array.isArray(dataSet.footnotes)) {
      dataSet.footnotes?.forEach((footnote: string | number): void => {
        if (!temporaryFootnoteMapping[footnote]) {
          temporaryFootnoteMapping[footnote] = [];
        }
        if (!temporaryFootnoteMapping[footnote].includes(dataSet.id)) {
          temporaryFootnoteMapping[footnote].push(dataSet.id);
        }
      });
    }
  }
}
